"use client";

import { useCallback, useMemo } from "react";

import { endIdentityTrace, identityFetch } from "@/lib/wallet/identity-trace";
import { messageForAddress } from "@/lib/auth/siws";
import { isRejection } from "@/lib/error-matching";
import { signInWithWallet, supportsSignIn } from "@/lib/wallet/phantom-sign-in";
import { NoSolanaAccountError } from "@/lib/wallet/solana/errors";
import { rememberDeclinedSignIn } from "@/lib/wallet/phantom-trusted";
import { reportedError } from "@/lib/wallet/untrusted-text";
import { trackConnectProofRecording } from "@/lib/wallet/connect-proof-recording";
import {
  identityAttempt,
  identityFailure,
  identityPhase,
  identityResponse,
} from "@/lib/wallet/identity-diagnostics";
import { signInFailed } from "@/lib/wallet/wallet-telemetry";
import { announceProof } from "@/lib/wallet/proof-channel";

import type { IdentityAttempt } from "@/lib/wallet/identity-diagnostics";
import type { SignInResult, SignInSource } from "@/lib/wallet/phantom-sign-in";

/**
 * What came of asking the wallet to sign in as part of connecting.
 *
 * `declined` is kept apart from `unavailable` because the two must lead
 * somewhere different: a holder who said no is not asked again, and a wallet
 * that cannot do it falls back to a signed message. `no-account` is a wallet
 * that connected without sharing a Solana account (MetaMask with Solana
 * unticked): connecting again would only repeat that question.
 */
export type SignInOutcome =
  "signed-in" | "declined" | "unavailable" | "no-account";

/** What the challenge endpoint hands back. */
interface Challenge {
  nonce: string;
  /** The one sentence the wallet shows above the details it renders itself. */
  statement: string;
  /** Composed and stored by the server, with the address a placeholder. */
  messageText: string;
}

/** The fallback signer, for a wallet with no `solana:signIn`. */
interface MessageSigner {
  address: string;
  signMessage: (message: Uint8Array) => Promise<Uint8Array>;
}

const CHALLENGE_URL = "/api/solana-session/challenge/";
// Under /api/game, so the browser sends its game cookie and the seat it holds is kept.
const VERIFY_URL = "/api/game/identity/solana/";
const TIMEOUT_MS = 15_000;

function base64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/**
 * Sign-In-With-Solana, recorded server-side.
 *
 * Two routes to the same signature, preferring the one that costs the holder
 * less. `solana:signIn` connects and authenticates in a single approval and
 * lets the wallet render its own message, which is what the spec intends and
 * what Phantom insists on — it validates a SIWS-shaped message handed to
 * plain `signMessage` and refuses one whose chain disagrees with the cluster
 * the holder is on. `signMessage` remains for a wallet without the feature,
 * and costs a second prompt.
 *
 * Either way the server reads the fields out of whatever text came back
 * rather than comparing it to its own rendering, so the two routes verify
 * identically.
 *
 * Failure is deliberately silent. The row is bookkeeping, not an
 * authorization gate: the Mudwater API takes no HTTP auth and every
 * privileged action is already a wallet signature, so a holder whose sign-in
 * could not be recorded must still be able to use the site.
 *
 * `source` is the wallet asked: Phantom's injected provider unless the
 * generic layer names its chosen wallet. Keep it stable between renders.
 */
/** Resolve a mutable source before awaiting the challenge, keeping this request's wallet. */
function pinnedSource(
  source: SignInSource | undefined
): SignInSource | undefined {
  if (!source) return undefined; // Preserve the legacy injected-provider path.
  const sign = source();
  return () => sign;
}

export function useSolanaSignIn(source?: SignInSource) {
  /** Fetches a challenge and signs it in a single wallet approval. */
  const signInAtConnect = useCallback(
    async (isCurrent: () => boolean = () => true): Promise<SignInOutcome> => {
      const selected = pinnedSource(source);
      if (!isCurrent() || !supportsSignIn(selected)) return "unavailable";
      const attempt = identityAttempt("connect");
      let challenge: Challenge | null = null;
      let recordingPending = false;
      try {
        challenge = await requestChallenge(attempt);
        if (!challenge || !isCurrent()) return "unavailable";
        const result = await signInWithWallet(
          { nonce: challenge.nonce, statement: challenge.statement },
          selected,
          attempt
        );
        if (!result || !isCurrent()) return "unavailable";
        /* The holder has approved. Recording is bookkeeping and may still fail,
         but the approval happened and asking for a second one over a write
         they cannot see would be nagging them for our problem. */
        const recording = postVerification(challenge.nonce, result, attempt)
          .then((ok) => {
            if (ok) return true;
            console.warn("[solana] the sign-in was not recorded");
            signInFailed("record", new Error("SIGN_IN_NOT_RECORDED"), attempt);
            return false;
          })
          // Fire and forget still has to catch: the outer `try` has already
          // returned by the time this settles, so a dropped connection here
          // would surface as an unhandled rejection rather than a warning.
          .catch((error: unknown) => {
            console.warn(
              "[solana] could not record the sign-in",
              reportedError(error)
            );
            signInFailed("record", error, attempt);
            return false;
          })
          .finally(() => endIdentityTrace(attempt));
        recordingPending = true;
        trackConnectProofRecording(result.address, recording);
        return "signed-in";
      } catch (error) {
        // A refusal is an answer, not a failure: never answered with another
        // prompt. Anything else leaves the fallback free to try.
        if (isRejection(error)) return "declined";
        if (error instanceof NoSolanaAccountError) return "no-account";
        console.warn(
          "[solana] could not sign in at connect",
          reportedError(error)
        );
        signInFailed("connect", error, attempt);
        /* Phantom answers `signIn` with a bare -32603 "Unexpected error" for
           some accounts (Ledger-backed among them), and the connect after it
           in the same press fails too. Asking again failed the same way on
           every press (Faro, 2026-10-30: 16 sessions, 4 never connected), so
           the next Connect in this tab is a plain one, as after a refusal. */
        rememberDeclinedSignIn();
        return "unavailable";
      } finally {
        if (!recordingPending) endIdentityTrace(attempt);
      }
    },
    [source]
  );

  /**
   * One `signIn` for an already-connected wallet, recorded before it answers:
   * the proof a caller is about to rely on (the claim's recipient check reads
   * the record next). False when declined or not recorded.
   */
  const signInNow = useCallback(
    async (isCurrent: () => boolean = () => true): Promise<boolean> => {
      const selected = pinnedSource(source);
      const attempt = identityAttempt("prove");
      try {
        if (!isCurrent()) return false;
        const challenge = await requestChallenge(attempt);
        if (!challenge || !isCurrent()) return false;
        const result = await signInWithWallet(
          { nonce: challenge.nonce, statement: challenge.statement },
          selected,
          attempt
        );
        return result && isCurrent()
          ? await postVerification(challenge.nonce, result, attempt)
          : false;
      } catch (error) {
        console.warn(
          "[solana] could not record the sign-in",
          reportedError(error)
        );
        if (!isRejection(error)) signInFailed("prove", error, attempt);
        return false;
      } finally {
        endIdentityTrace(attempt);
      }
    },
    [source]
  );

  /** The second-prompt route, for a wallet with no `signIn`. */
  const signInByMessage = useCallback(
    async (
      wallet: MessageSigner,
      isCurrent: () => boolean = () => true
    ): Promise<boolean> => {
      const attempt = identityAttempt("message");
      attempt.operation = "sign_message";
      const recording = (async () => {
        try {
          const challenge = await requestChallenge(attempt);
          if (!challenge || !isCurrent()) return false;
          const text = messageForAddress(challenge.messageText, wallet.address);
          const signedMessage = new TextEncoder().encode(text);
          identityPhase(attempt, "prompt");
          const signature = await wallet.signMessage(signedMessage);
          identityPhase(attempt, "returned", identityResponse(signature));
          if (!isCurrent()) return false;
          return await postVerification(
            challenge.nonce,
            {
              address: wallet.address,
              signedMessage,
              signature,
            },
            attempt
          );
        } catch (error) {
          console.warn(
            "[solana] could not record the sign-in",
            reportedError(error)
          );
          if (isRejection(error))
            identityPhase(attempt, "cancelled", identityFailure(error));
          else signInFailed("message", error, attempt);
          return false;
        } finally {
          endIdentityTrace(attempt);
        }
      })();
      trackConnectProofRecording(wallet.address, recording);
      return recording;
    },
    []
  );

  /* Memoised, not a fresh literal. `usePhantomWallet` takes this as a
     dependency of `connectSolana`, which feeds the memo that keeps the
     published `WalletState` stable — hand it a new object each render and
     every `useWallet()` consumer re-renders on every render of the bridge.
     The Glyph hook carries a comment about the same invariant for the same
     reason. */
  return useMemo(
    () => ({ signInAtConnect, signInByMessage, signInNow }),
    [signInAtConnect, signInByMessage, signInNow]
  );
}

async function requestChallenge(
  attempt: IdentityAttempt
): Promise<Challenge | null> {
  identityPhase(attempt, "challenge_started");
  const issued = await identityFetch(attempt, CHALLENGE_URL, {
    method: "POST",
    // The server takes only JSON, which a cross-site form cannot send.
    headers: { "content-type": "application/json" },
    body: "{}",
    signal: AbortSignal.timeout(TIMEOUT_MS),
  });
  identityPhase(attempt, "challenge_returned", {
    http_status: String(issued.status ?? "unknown"),
    outcome: issued.ok ? "ok" : "refused",
  });
  return issued.ok ? await issued.json() : null;
}

async function postVerification(
  nonce: string,
  result: SignInResult,
  attempt: IdentityAttempt
): Promise<boolean> {
  identityPhase(attempt, "verification_started");
  const verified = await identityFetch(attempt, VERIFY_URL, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      nonce,
      address: result.address,
      signedMessage: base64(result.signedMessage),
      signature: base64(result.signature),
    }),
    signal: AbortSignal.timeout(TIMEOUT_MS),
  });
  identityPhase(attempt, "verification_returned", {
    http_status: String(verified.status ?? "unknown"),
    outcome: verified.ok ? "ok" : "refused",
  });
  if (verified.ok) announceProof();
  return verified.ok;
}
